/**
 * Hook for touch gesture handling on mobile devices.
 * Supports single-finger pan, two-finger pinch zoom, and momentum scrolling.
 */

import { useCallback, useEffect, useRef } from "react";

import {
  applyMomentum,
  createVelocityTracker,
  detectTouchGesture,
  getTouchCenter,
  getTouchDistance,
  type VelocityTracker,
} from "~utils/ticker-chart";
import type { ChartStore, Viewport } from "~types/types";

/** Friction coefficient for momentum scrolling (0-1, higher = slower deceleration) */
const MOMENTUM_FRICTION = 0.95;

/** Minimum velocity (px/ms) to trigger momentum scrolling */
const MIN_MOMENTUM_VELOCITY = 0.1;

export interface UseTouchInteractionOptions {
  /** Ref to the element to attach touch listeners */
  elementRef: React.RefObject<HTMLElement | SVGElement | null>;
  /** Chart store for zoom/pan operations */
  store: ChartStore;
  /** Enable zoom via pinch gesture */
  zoomEnabled: boolean;
  /** Enable pan via single touch drag */
  panEnabled: boolean;
  /** Enable momentum scrolling after pan */
  momentumEnabled?: boolean;
}

interface TouchState {
  type: "idle" | "pan" | "pinch";
  /** Initial X position for pan (in chart coordinates) */
  startX: number;
  /** Initial viewport at touch start */
  initialViewport: Viewport;
  /** Initial distance between two touches for pinch */
  initialDistance: number;
  /** Initial center X for pinch (in screen coordinates) */
  initialCenterX: number;
}

/**
 * Hook that attaches touch event listeners for pan and zoom.
 * Handles:
 * - Single finger: pan left/right with momentum
 * - Two fingers: pinch to zoom in/out
 */
export function useTouchInteraction({
  elementRef,
  store,
  zoomEnabled,
  panEnabled,
  momentumEnabled = true,
}: UseTouchInteractionOptions): void {
  const touchStateRef = useRef<TouchState>({
    type: "idle",
    startX: 0,
    initialViewport: { start: 0, end: 0 },
    initialDistance: 0,
    initialCenterX: 0,
  });

  const velocityTrackerRef = useRef<VelocityTracker | null>(null);
  const momentumCancelRef = useRef<{ cancel: () => void } | null>(null);

  // Initialize velocity tracker on mount
  useEffect(() => {
    velocityTrackerRef.current = createVelocityTracker(100);

    return () => {
      momentumCancelRef.current?.cancel();
    };
  }, []);

  const getChartX = useCallback(
    (screenX: number, rect: DOMRect): number => {
      const layout = store.getLayout();

      return screenX - rect.left + layout.priceArea.x;
    },
    [store]
  );

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      // Cancel any ongoing momentum
      momentumCancelRef.current?.cancel();
      momentumCancelRef.current = null;

      const el = elementRef.current;

      if (!el) return;

      const rect = el.getBoundingClientRect();
      const gestureType = detectTouchGesture(event.touches.length);

      if (gestureType === "pan" && panEnabled) {
        event.preventDefault();

        const touch = event.touches[0];
        const chartX = getChartX(touch.clientX, rect);

        touchStateRef.current = {
          type: "pan",
          startX: chartX,
          initialViewport: { ...store.getViewport() },
          initialDistance: 0,
          initialCenterX: 0,
        };

        velocityTrackerRef.current?.reset();
        velocityTrackerRef.current?.track(chartX);

        store.setDragging(true);

        // Hide crosshair during pan interactions
        store.clearHover();
      } else if (gestureType === "pinch" && zoomEnabled) {
        event.preventDefault();

        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const distance = getTouchDistance(touch1, touch2);
        const center = getTouchCenter(touch1, touch2);

        touchStateRef.current = {
          type: "pinch",
          startX: 0,
          initialViewport: { ...store.getViewport() },
          initialDistance: distance,
          initialCenterX: center.x,
        };

        store.setPinching(true);

        // Hide crosshair during pinch zoom interactions
        store.clearHover();
      }
    },
    [elementRef, store, panEnabled, zoomEnabled, getChartX]
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      const el = elementRef.current;

      if (!el) return;

      const state = touchStateRef.current;
      const rect = el.getBoundingClientRect();

      if (state.type === "pan" && panEnabled && event.touches.length === 1) {
        event.preventDefault();

        const touch = event.touches[0];
        const currentChartX = getChartX(touch.clientX, rect);

        // Track velocity for momentum
        velocityTrackerRef.current?.track(currentChartX);

        // Calculate delta from original start position
        const deltaX = state.startX - currentChartX;
        const { initialViewport } = state;
        const layout = store.getLayout();
        const viewportRange = initialViewport.end - initialViewport.start;
        const pixelsPerMs = layout.priceArea.width / viewportRange;
        const deltaMs = deltaX / pixelsPerMs;

        const newViewport: Viewport = {
          start: initialViewport.start + deltaMs,
          end: initialViewport.end + deltaMs,
        };

        store.setViewportWithoutSnap(newViewport);
      } else if (
        state.type === "pinch" &&
        zoomEnabled &&
        event.touches.length === 2
      ) {
        event.preventDefault();

        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const currentDistance = getTouchDistance(touch1, touch2);
        const center = getTouchCenter(touch1, touch2);

        // Calculate scale factor
        const scaleFactor = currentDistance / state.initialDistance;

        // Calculate chart X for zoom anchor
        const chartX = getChartX(center.x, rect);

        // Apply zoom
        store.zoom(scaleFactor, chartX);

        // Update initial distance for continuous pinch
        touchStateRef.current.initialDistance = currentDistance;
      }
    },
    [elementRef, store, panEnabled, zoomEnabled, getChartX]
  );

  const handleTouchEnd = useCallback(
    (_event: TouchEvent) => {
      const state = touchStateRef.current;

      if (state.type === "pan" && panEnabled) {
        store.setDragging(false);

        // Apply momentum if enabled and velocity is sufficient
        if (momentumEnabled) {
          const velocity = velocityTrackerRef.current?.getVelocity() ?? 0;

          if (Math.abs(velocity) > MIN_MOMENTUM_VELOCITY) {
            // Scale to per-frame velocity (16ms)
            momentumCancelRef.current = applyMomentum(
              -velocity * 16,
              (frameVelocity) => {
                store.panWithoutSnap(frameVelocity);
              },
              // Snap to data points when momentum ends
              () => {
                store.snapViewport();
              },
              MOMENTUM_FRICTION,
              0.5
            );
          } else {
            // No momentum - snap immediately
            store.snapViewport();
          }
        } else {
          // Momentum disabled - snap immediately
          store.snapViewport();
        }
      } else if (state.type === "pinch" && zoomEnabled) {
        store.setPinching(false);
        // Snap to data points at end of pinch
        store.snapViewport();
      }

      // Reset touch state
      touchStateRef.current = {
        type: "idle",
        startX: 0,
        initialViewport: { start: 0, end: 0 },
        initialDistance: 0,
        initialCenterX: 0,
      };
    },
    [store, panEnabled, zoomEnabled, momentumEnabled]
  );

  const handleTouchCancel = useCallback(() => {
    momentumCancelRef.current?.cancel();
    momentumCancelRef.current = null;

    store.setDragging(false);
    store.setPinching(false);

    // Reset touch state
    touchStateRef.current = {
      type: "idle",
      startX: 0,
      initialViewport: { start: 0, end: 0 },
      initialDistance: 0,
      initialCenterX: 0,
    };
    // No snap on cancel - viewport stays where it is
  }, [store]);

  useEffect(() => {
    const el = elementRef.current;

    if (!el) return undefined;

    // Cast handlers to EventListener since addEventListener expects generic Event type
    const touchStartHandler = handleTouchStart as EventListener;
    const touchMoveHandler = handleTouchMove as EventListener;
    const touchEndHandler = handleTouchEnd as EventListener;
    const touchCancelHandler = handleTouchCancel as EventListener;

    // Use passive: false to allow preventDefault
    el.addEventListener("touchstart", touchStartHandler, { passive: false });
    el.addEventListener("touchmove", touchMoveHandler, { passive: false });
    el.addEventListener("touchend", touchEndHandler);
    el.addEventListener("touchcancel", touchCancelHandler);

    return () => {
      el.removeEventListener("touchstart", touchStartHandler);
      el.removeEventListener("touchmove", touchMoveHandler);
      el.removeEventListener("touchend", touchEndHandler);
      el.removeEventListener("touchcancel", touchCancelHandler);

      momentumCancelRef.current?.cancel();
    };
  }, [
    elementRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
  ]);
}
